import { useCallback, useMemo, useRef } from 'react'
import type { PersonalNoteDocument } from '@/lib/db/rxdb/hooks/entities/personal-notes'
import type { PersonalNoteContentDocument } from '@/lib/db/rxdb/hooks/entities/personal-note-content'
import { TiptapRichTextEditor } from '@/components/editor/TiptapRichTextEditor'
import { useEditorTaskCreator } from '@/hooks/useEditorTaskCreator'
import { useEditorAutoSave } from '@/components/editor/useEditorAutoSave'
import { DEFAULT_FORMAT_TOOLBAR_ITEMS } from '@/components/editor/toolbarDefaults'
import {
  useEntityLinkMutations,
  useEntityLinks,
} from '@/lib/db/rxdb/hooks'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import useLoginStore from '@/store/LoginStore'

const EDITOR_NAMESPACE = 'NoteDescriptionEditor'

interface NoteDescriptionEditorProps {
  /** Existing note, or null if not yet created */
  note: PersonalNoteDocument | null
  content: PersonalNoteContentDocument | null
  canEdit: boolean
  /** Called on first edit to create the note record. Returns the new note ID. */
  createNoteIfNeeded: () => Promise<string | null>
  createPersonalNoteContent: (
    data: Partial<PersonalNoteContentDocument>,
  ) => Promise<unknown>
  updatePersonalNoteContent: (
    id: string,
    data: Partial<PersonalNoteContentDocument>,
  ) => Promise<unknown>
}

/**
 * TipTap editor wrapper for personal note content.
 * Creates the note on first content change if it doesn't exist yet.
 */
export function NoteDescriptionEditor({
  note,
  content,
  canEdit,
  createNoteIfNeeded,
  createPersonalNoteContent,
  updatePersonalNoteContent,
}: NoteDescriptionEditorProps) {
  const { links: existingLinks } = useEntityLinks(note?.id)
  // Tasks created from a note go to the Inbox (a note has no project).
  const createTask = useEditorTaskCreator(null)
  const editorActions = useMemo(() => ({ createTask }), [createTask])
  const { createEntityLink, deleteEntityLink } = useEntityLinkMutations()
  const userId = useLoginStore((state) => state.userId)
  const noteIdRef = useRef<string | null>(note?.id ?? null)

  // Keep ref in sync with the current note — including resetting to null
  // when the note clears (e.g. navigating to a date that has no note yet),
  // so auto-save doesn't attach new content to the previous note.
  if (noteIdRef.current !== (note?.id ?? null)) {
    noteIdRef.current = note?.id ?? null
  }

  const createContent = useCallback(
    async (data: {
      content: string
      format: string
      length: number
      plainText: string
      version: number
    }) => {
      // Create the note first if it doesn't exist
      let id = noteIdRef.current
      if (!id) {
        id = await createNoteIfNeeded()
        if (!id) return
        noteIdRef.current = id
      }
      await createPersonalNoteContent({
        personalNoteId: id,
        ...data,
      })
    },
    [createNoteIfNeeded, createPersonalNoteContent],
  )

  const updateContent = useCallback(
    async (
      id: string,
      data: {
        content: string
        format: string
        length: number
        plainText: string
        version: number
      },
    ) => {
      await updatePersonalNoteContent(id, data)
    },
    [updatePersonalNoteContent],
  )

  const { displayContent, handleContentChange } = useEditorAutoSave({
    entityId: note?.id,
    entityType: ENTITY_TYPES.PERSONAL_NOTE,
    entityName: note?.title || '',
    envId: note?.envId,
    userId,
    content,
    existingLinks,
    canEdit,
    createContent,
    updateContent,
    createEntityLink,
    deleteEntityLink,
  })

  // Stable key: use note ID if available, otherwise a stable placeholder
  const editorKey = note?.id
    ? `${note.id}-${content?.id || 'no-content'}`
    : 'new-note'

  return (
    <div className="flex-1 min-w-0">
      <TiptapRichTextEditor
        key={editorKey}
        namespace={`${EDITOR_NAMESPACE}-${note?.id || 'new'}`}
        initialContent={displayContent}
        format={
          (content?.format as 'tiptap' | 'markdown' | 'plain' | undefined)
        }
        editable={canEdit}
        placeholder="Start writing..."
        contentEditableClassName="py-0 min-h-[60vh]"
        placeholderClassName="top-0"
        onContentChange={handleContentChange}
        ariaLabel="Note content editor"
        toolbarItems={DEFAULT_FORMAT_TOOLBAR_ITEMS}
        enableSlashCommands
        enableMentions
        enableEntityLinks
        enableTables
        actions={editorActions}
      />
    </div>
  )
}
